// kanjo/Price.jsx — Multi-vendor price compare wired to live priceBook (US-2.6).
// Style aligned with the mockup design system: 12-week price history mini chart
// + vendor rows with avatar + "Cheapest right now" pill.

function fmtMoneyP(cents, currency, locale) {
  const c = window.kanjo?.demo?.scale ? window.kanjo.demo.scale(cents) : cents;
  return new Intl.NumberFormat(locale || 'en-US', { style: 'currency', currency: currency || 'USD' })
    .format((c || 0) / 100);
}

const PRICE_PALETTE = ['#0E7C5C', '#1F3A68', '#B45309', '#7A5AE0', '#0a8a8c', '#605c52'];
function vendorColor(vendorId) {
  let h = 0;
  for (let i = 0; i < vendorId.length; i++) h = (h * 31 + vendorId.charCodeAt(i)) >>> 0;
  return PRICE_PALETTE[h % PRICE_PALETTE.length];
}

// ── PriceMiniChart: smooth line over up-to-12 weekly samples ─────────────
function PriceMiniChart({ points, currency, locale, w = 320, h = 96 }) {
  if (!points || points.length < 2) {
    return (
      <div style={{ height: h, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: 'var(--ink-3)' }}>
        Not enough history yet.
      </div>
    );
  }
  const padX = 6, padT = 14, padB = 22;
  const innerW = w - padX * 2;
  const innerH = h - padT - padB;
  const min = Math.min(...points);
  const max = Math.max(...points);
  const span = Math.max(1, max - min);
  const step = innerW / (points.length - 1);
  const norm = (v) => padT + innerH - ((v - min) / span) * innerH;
  let d = '';
  points.forEach((v, i) => {
    const x = padX + i * step;
    const y = norm(v);
    d += (i ? ' L ' : 'M ') + x.toFixed(1) + ' ' + y.toFixed(1);
  });
  const lastX = padX + (points.length - 1) * step;
  const lastY = norm(points[points.length - 1]);
  const fmt = (c) => new Intl.NumberFormat(locale || 'en-US', { style: 'currency', currency: currency || 'USD' }).format(c / 100);
  return (
    <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" aria-hidden="true" className="spark">
      {/* Min / max gridlines */}
      <line x1={padX} y1={padT} x2={w - padX} y2={padT} stroke="var(--rule)" strokeWidth="0.5"/>
      <line x1={padX} y1={padT + innerH} x2={w - padX} y2={padT + innerH} stroke="var(--rule)" strokeWidth="0.5"/>
      <text x={w - padX} y={padT - 4} textAnchor="end" fontSize="10" fill="var(--ink-4)" fontFamily="var(--mono)">{fmt(max)}</text>
      <text x={w - padX} y={padT + innerH + 12} textAnchor="end" fontSize="10" fill="var(--ink-4)" fontFamily="var(--mono)">{fmt(min)}</text>
      {/* Line */}
      <path d={d} fill="none" stroke="var(--brand)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx={lastX} cy={lastY} r="3.4" fill="var(--brand)"/>
      <circle cx={lastX} cy={lastY} r="6" fill="var(--brand)" opacity="0.18"/>
    </svg>
  );
}

function usePriceCompare() {
  const [state, setState] = React.useState({ loading: true, item: null, rows: [], best: null, history: [], currency: 'USD', locale: 'en-US' });
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!window.kanjo) return;
      const hash = window.location.hash || '';
      const q = new URLSearchParams(hash.includes('?') ? hash.split('?')[1] : '');
      const itemId = q.get('id');
      try {
        const claims = await window.kanjo.auth.currentClaims().catch(() => null);
        if (!claims) { setState((s) => ({ ...s, loading: false })); return; }
        const profile = await window.kanjo.db.userProfile.get(claims.sub);
        let item = null;
        if (itemId) item = await window.kanjo.db.items.get(itemId);
        if (!item) {
          const list = await window.kanjo.procurement.items.listActive();
          item = list[0];
        }
        if (!item || cancelled) { setState((s) => ({ ...s, loading: false })); return; }
        const [rows, best, history] = await Promise.all([
          window.kanjo.procurement.priceBook.compareVendors(item.itemId),
          window.kanjo.procurement.priceBook.lastBestPrice(item.itemId),
          window.kanjo.procurement.priceBook.history(item.itemId, { months: 3 }),
        ]);
        // Bucket history into roughly weekly samples (last 12) using lowest price per week across vendors.
        const byWeek = new Map();
        for (const h of history) {
          const d = new Date(h.effectiveDate + 'T00:00:00Z');
          const monday = new Date(d); monday.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7));
          const wk = monday.toISOString().slice(0, 10);
          const cur = byWeek.get(wk);
          const cents = h.unitPriceCents ?? h.priceCents;
          if (!cur || cents < cur) byWeek.set(wk, cents);
        }
        const weeklyPoints = [...byWeek.entries()].sort(([a], [b]) => a.localeCompare(b)).slice(-12).map(([, v]) => v);
        if (!cancelled) setState({
          loading: false, item, rows, best, history: weeklyPoints,
          currency: profile?.currency || 'USD',
          locale: profile?.locale || 'en-US',
        });
      } catch (e) {
        console.warn('[PriceCompare] load failed', e);
        if (!cancelled) setState((s) => ({ ...s, loading: false }));
      }
    })();
    return () => { cancelled = true; };
  }, []);
  return state;
}

function PriceCompare({ dark = false, persona = 'owner' }) {
  const s = usePriceCompare();
  if (s.loading) return (
    <KShell dark={dark} tab="buy" persona={persona}>
      <KHeader back big title="Compare prices" subtitle="Loading…" />
    </KShell>
  );
  if (!s.item) return (
    <KShell dark={dark} tab="buy" persona={persona}>
      <KHeader back big title="Compare prices" subtitle="No items yet" />
    </KShell>
  );

  return (
    <KShell dark={dark} tab="buy" persona={persona}>
      <div style={{ height: '100%', overflowY: 'auto', paddingBottom: 16 }}>
        <KHeader back big title={s.item.name} subtitle={s.item.packSize || s.item.defaultUom} />

        {/* History chart */}
        <div style={{ padding: '0 20px 14px' }}>
          <div className="card" style={{ padding: 18 }}>
            <div className="label">Last 12 weeks (best price)</div>
            <div style={{ marginTop: 10 }}>
              <PriceMiniChart points={s.history} currency={s.currency} locale={s.locale}/>
            </div>
            {s.best && (
              <div style={{ marginTop: 6, fontSize: 12, color: 'var(--ink-3)' }}>
                Floor: <span className="mono" style={{ color: 'var(--ink-2)', fontWeight: 500 }}>{fmtMoneyP(s.best.priceCents, s.currency, s.locale)}</span> on {s.best.effectiveDate}
              </div>
            )}
          </div>
        </div>

        {/* Vendor list */}
        {s.rows.length === 0 ? (
          <div style={{ padding: 24, color: 'var(--on-dim)', fontSize: 13 }}>
            No vendor prices recorded for this item yet. Scan an invoice that includes it.
          </div>
        ) : (
          <div style={{ padding: '0 20px 14px' }}>
            <div className="label" style={{ marginBottom: 10, paddingLeft: 4 }}>By vendor</div>
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              {s.rows.map((r, i) => {
                const color = vendorColor(r.vendorId);
                return (
                  <div key={r.vendorId} style={{
                    display: 'grid', gridTemplateColumns: '32px 1fr auto', gap: 12,
                    padding: '14px 16px',
                    borderBottom: i < s.rows.length - 1 ? '1px solid var(--rule)' : 'none',
                    alignItems: 'center',
                  }}>
                    <div style={{
                      width: 32, height: 32, borderRadius: 9,
                      background: color, color: '#fff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 13, fontWeight: 600,
                    }}>{(r.vendorName || '?')[0]?.toUpperCase()}</div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                        <div style={{ fontSize: 15, fontWeight: 500 }}>{r.vendorName}</div>
                        {r.isCheapest && <span className="pill positive" style={{ fontSize: 10.5, padding: '2px 7px' }}>Cheapest now</span>}
                      </div>
                      <div style={{ fontSize: 12.5, color: 'var(--ink-3)', marginTop: 2 }}>
                        {r.packSize || ''}
                        {r.unitPriceCents != null && r.packSize ? ' · ' : ''}
                        {r.unitPriceCents != null ? <span className="mono">{fmtMoneyP(r.unitPriceCents, s.currency, s.locale)}/unit</span> : null}
                      </div>
                      {r.lastPurchased && (
                        <div style={{ fontSize: 11.5, color: 'var(--ink-4)', marginTop: 2 }}>
                          Last bought {r.lastPurchased}
                        </div>
                      )}
                    </div>
                    <div className="mono" style={{ fontSize: 17, fontWeight: 500, textAlign: 'right' }}>
                      {fmtMoneyP(r.currentPriceCents, s.currency, s.locale)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </KShell>
  );
}

Object.assign(window, { PriceCompare, PriceMiniChart });
